/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SelectChangeEvent } from '@mui/material';
import {
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Box,
  LinearProgress,
} from '@mui/material';
import React from 'react';
import {
  AlertBanner,
  StyledSelect,
  ToolContainerDraggable,
} from '@opengeoweb/shared';
import type { SourceSelection } from '@opengeoweb/shared';
import { useSoundingsTranslation } from '../../utils/i18n';
import type { SoundingsCollectionsWithInstances } from '../Soundings/types';
import { getDisplayValue } from '../utils';

const extractVerticalRange = (
  collection: SoundingsCollectionsWithInstances,
  selectedInstanceId: string,
): string => {
  const instance = collection.instances.find(
    (instance) => instance.id === selectedInstanceId,
  );
  const interval = instance?.extent?.vertical?.interval;
  if (interval) {
    // Fallback for FMI server while it sends a string array instead of string[][] as specified in documentation
    if (typeof interval[0] === 'string') {
      const verticalDims =
        (interval as unknown as string[]).toSorted((dimA, dimB) =>
          dimA.localeCompare(dimB, undefined, { numeric: true }),
        ) ?? [];
      return verticalDims.join('/');
    }

    const firstEl = interval[0][0];
    const lastEl = interval[interval.length - 1][1];
    const verticalDims =
      [firstEl, lastEl].toSorted((dimA, dimB) =>
        dimA.localeCompare(dimB, undefined, { numeric: true }),
      ) ?? [];
    return verticalDims.join('/');
  }
  return '';
};

interface SoundingsSourceMenuProps {
  soundingsCollections: SoundingsCollectionsWithInstances[] | undefined;
  isOpen?: boolean;
  handleClose: () => void;
  updateCollection: (sourceId: string, value: SourceSelection) => void;
  sourceSelections: Record<string, SourceSelection>;
  onMouseDown?: () => void;
  order?: number;
  isLoading?: boolean;
  error?: Error | null;
}

export const SoundingsSourceMenu = ({
  soundingsCollections,
  isOpen = false,
  handleClose,
  updateCollection,
  sourceSelections,
  onMouseDown = (): void => {},
  order = 0,
  isLoading = false,
  error,
}: SoundingsSourceMenuProps): React.ReactElement => {
  const { t } = useSoundingsTranslation();

  return (
    <ToolContainerDraggable
      bounds="parent"
      className="source-menu"
      isOpen={isOpen}
      minWidth={300}
      onClose={handleClose}
      onMouseDown={onMouseDown}
      order={order}
      startPosition={{ top: 40, left: 50 }}
      startSize={{ width: 320 }}
      title={t('sources')}
    >
      {!!isLoading && (
        <LinearProgress sx={{ width: '100%', position: 'absolute' }} />
      )}
      {!!error && <AlertBanner shouldClose title={error.message} />}
      <List sx={{ pr: 1 }}>
        {soundingsCollections && soundingsCollections.length > 0 ? (
          soundingsCollections.map((collection) => {
            const { isChecked, instanceId, url } =
              sourceSelections[collection.id] || {};
            const sourceUrl = url || collection.url;
            const hasInstances = collection.instances.length > 0;
            const selectedInstanceId =
              instanceId || collection.instances[0]?.id || undefined;
            return (
              <ListItem disablePadding key={collection.id}>
                <ListItemIcon
                  sx={{
                    minWidth: 42,
                  }}
                >
                  <Checkbox
                    checked={!!isChecked}
                    disableRipple
                    onChange={(event) => {
                      const verticalRange =
                        selectedInstanceId !== undefined
                          ? extractVerticalRange(collection, selectedInstanceId)
                          : '';

                      updateCollection(collection.id, {
                        url: sourceUrl,
                        isChecked: event.target.checked,
                        instanceId: selectedInstanceId,
                        verticalRange,
                      });
                    }}
                  />
                </ListItemIcon>
                <StyledSelect
                  additionalStyling={{ width: 'calc(100% - 42px)' }}
                  isEnabled
                  onChange={(event: SelectChangeEvent<unknown>) => {
                    const instanceId = (event as SelectChangeEvent<string>)
                      .target.value;
                    const verticalRange = extractVerticalRange(
                      collection,
                      instanceId,
                    );
                    updateCollection(collection.id, {
                      url: sourceUrl,
                      instanceId,
                      verticalRange,
                    });
                  }}
                  renderValue={(value: unknown) => (
                    <Box
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                        }}
                      >
                        <Box sx={{ minWidth: 200 }}>{collection.name}</Box>

                        <Box
                          sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >
                          {getDisplayValue(value as string)}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  skipLocalStyling={false}
                  value={selectedInstanceId || t('noInstancesAvailable')}
                >
                  {!hasInstances && (
                    <MenuItem
                      disabled
                      sx={{ fontSize: 12 }}
                      value={t('noInstancesAvailable')}
                    >
                      {t('noInstancesAvailable')}
                    </MenuItem>
                  )}
                  {collection.instances.map((instance) => (
                    <MenuItem key={instance.id} value={instance.id}>
                      {getDisplayValue(instance.id)}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </ListItem>
            );
          })
        ) : (
          <ListItem>
            <ListItemText primary={t('noServicesFound')} sx={{ pl: 4 }} />
          </ListItem>
        )}
      </List>
    </ToolContainerDraggable>
  );
};
